import { useCallback, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { DOMAIN_TYPE_OPTIONS, NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import type { DomainTypeOption } from '@/shared/lib/constants';
import { domainKeys } from '@/shared/lib/query/queryKeys';

import type { DomainItem } from '@/entities/domain';

import { updateDomainType } from '@/features/domain/api';

type UseDomainTypeEditProps = {
  domainId: string;
  domainFqn: string;
  currentDomainType: string;
};

export type DomainTypeEditHook = {
  isOpen: boolean;
  selectedDomainType: string;
  domainTypeOptions: DomainTypeOption[];
  isUpdating: boolean;
  hasChanges: boolean;
  handleOpen: () => void;
  handleClose: () => void;
  handleDomainTypeSelect: (domainType: string) => void;
  handleUpdate: () => void;
};

export const useDomainTypeEdit = ({
  domainId,
  domainFqn,
  currentDomainType,
}: UseDomainTypeEditProps): DomainTypeEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDomainType, setSelectedDomainType] = useState(currentDomainType);

  const updateDomainTypeMutation = useMutation({
    mutationFn: async (newDomainType: string) => {
      await updateDomainType(domainId, newDomainType);
    },
    onMutate: async (newDomainType) => {
      await queryClient.cancelQueries({ queryKey: domainKeys.detail(domainFqn) });
      const previousDomain = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));

      if (previousDomain) {
        queryClient.setQueryData<DomainItem>(domainKeys.detail(domainFqn), {
          ...previousDomain,
          domainType: newDomainType,
        });
      }

      return { previousDomain };
    },
    onError: (error, _variables, context) => {
      if (context?.previousDomain) {
        queryClient.setQueryData(domainKeys.detail(domainFqn), context.previousDomain);
      }
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_TYPE_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.DOMAIN_TYPE_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: domainKeys.detail(domainFqn) });
      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_TYPE_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_TYPE_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 2,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestDomainData = queryClient.getQueryData<DomainItem>(domainKeys.detail(domainFqn));
    if (latestDomainData) {
      setSelectedDomainType(latestDomainData.domainType);
    }
  }, [queryClient, domainFqn]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSelectedDomainType(currentDomainType);
  }, [currentDomainType]);

  const handleDomainTypeSelect = useCallback((domainType: string) => {
    setSelectedDomainType(domainType);
  }, []);

  const handleUpdate = useCallback(() => {
    updateDomainTypeMutation.mutate(selectedDomainType, {
      onSuccess: () => {
        setIsOpen(false);
      },
    });
  }, [selectedDomainType, updateDomainTypeMutation]);

  const hasChanges = useMemo(() => {
    return currentDomainType !== selectedDomainType;
  }, [currentDomainType, selectedDomainType]);

  return {
    isOpen,
    selectedDomainType,
    domainTypeOptions: DOMAIN_TYPE_OPTIONS,
    isUpdating: updateDomainTypeMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    handleDomainTypeSelect,
    handleUpdate,
  };
};
